import React, { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth, roleHome } from "@/context/AuthContext";
import { Brand } from "@/components/SiteHeader";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, AlertCircle } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";

const USER_IMG = "https://images.unsplash.com/photo-1758691462858-f1286e5daf40?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";
const BLITZER_IMG = "https://customer-assets-v7afamib.emergentagent.net/job_info-vault-27/artifacts/ctchi280_New_blitzer_steps.jpg";

export default function Register() {
  const { user, register, registerBusiness } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const isUser = searchParams.get("type") === "user"; // end-user account (no business)

  const [businessName, setBusinessName] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user && user !== false) navigate(roleHome(user), { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    let res;
    if (isUser) {
      res = await register(name, email, password, undefined);
    } else {
      if (!businessName.trim()) { setError("Please enter your Blitzer name."); setLoading(false); return; }
      res = await registerBusiness(businessName, name, email, password);
    }
    setLoading(false);
    if (!res.ok) { setError(res.error); return; }
    if (isUser) {
      const pending = searchParams.get("join") || localStorage.getItem("pending_join");
      if (pending) {
        try {
          await api.post("/patient/join", null, { params: { clinic_id: pending } });
          localStorage.removeItem("pending_join");
          navigate(`/dashboard?clinic=${pending}`, { replace: true });
          return;
        } catch (err) {
          toast.error("We couldn't link that business to your account. Open the check-in link again from your Blitzer Businesses page to retry.");
        }
      }
      navigate("/my-companies", { replace: true });
    } else {
      navigate("/admin/onboarding", { replace: true });
    }
  };

  const sideImg = isUser ? USER_IMG : BLITZER_IMG;

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="relative hidden lg:block">
        <img src={sideImg} alt="BlitzEntry" className={`h-full w-full ${isUser ? "object-cover" : "bg-slate-900 object-contain"}`} />
        {isUser && <div className="absolute inset-0 bg-gradient-to-t from-[#0B3D8A]/70 via-[#0F52BA]/20 to-transparent" />}
        {isUser && (
          <div className="absolute bottom-12 left-12 max-w-sm text-white">
            <p className="text-2xl font-semibold leading-snug">Welcome — let's get you checked in.</p>
            <p className="mt-2 text-blue-100">Create your secure account to complete your paperwork.</p>
          </div>
        )}
      </div>

      <div className="flex flex-col justify-center px-6 py-12 sm:px-12 lg:px-20">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="mx-auto w-full max-w-sm">
          <Brand />
          <h1 className="mt-10 text-3xl font-bold tracking-tight text-slate-900">
            {isUser ? "Create your account" : "Set up New Nimbus Account"}
          </h1>
          <p className="mt-2 text-slate-500">
            {isUser ? "It only takes a minute to get started." : "Set up your BlitzEntry workspace in under a minute."}
          </p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            {error && (
              <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="register-error">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /> <span>{error}</span>
              </div>
            )}
            {!isUser && (
              <div>
                <Label htmlFor="businessName" className="mb-2 block font-semibold text-slate-700">Business name</Label>
                <Input id="businessName" required value={businessName} onChange={(e) => setBusinessName(e.target.value)} placeholder="e.g. Shreveport Eye Specialists" className="h-12" data-testid="register-business-name" />
              </div>
            )}
            <div>
              <Label htmlFor="name" className="mb-2 block font-semibold text-slate-700">{isUser ? "Full name" : "Your name"}</Label>
              <Input id="name" required value={name} onChange={(e) => setName(e.target.value)} className="h-12" data-testid="register-name" />
            </div>
            <div>
              <Label htmlFor="email" className="mb-2 block font-semibold text-slate-700">{isUser ? "Email" : "Work email"}</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-12" data-testid="register-email" />
            </div>
            <div>
              <Label htmlFor="password" className="mb-2 block font-semibold text-slate-700">Password</Label>
              <Input id="password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="h-12" data-testid="register-password" />
              <p className="mt-1.5 text-xs text-slate-400">At least 6 characters.</p>
            </div>
            <Button type="submit" disabled={loading} className="h-12 w-full bg-[#0F52BA] text-base hover:bg-[#0B3D8A]" data-testid="register-submit">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : isUser ? "Create account" : "Start free trial"}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-slate-500">
            {isUser ? (
              <>Are you a business?{" "}
                <Link to="/register" className="font-semibold text-[#0F52BA] hover:underline" data-testid="goto-business">Create a Nimbus account</Link>
              </>
            ) : (
              <>Signing in as a customer?{" "}
                <Link to="/register?type=user" className="font-semibold text-[#0F52BA] hover:underline" data-testid="goto-user">Create a personal account</Link>
              </>
            )}
          </p>
          <p className="mt-3 text-center text-sm text-slate-500">
            Already a Blitzer (user)?{" "}
            <Link to="/login" className="font-semibold text-[#0F52BA] hover:underline" data-testid="goto-login">
              Sign in
            </Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
